/**
 * ADServiceDnsSection — DNS del dominio: zonas y reenviadores.
 *
 * AD y DNS van de la mano (el dominio se sostiene sobre zonas integradas en
 * Active Directory), pero la topología AD no contemplaba DNS en absoluto.
 * Se documenta en `Service.config_json.ad_topology.dns`:
 *   - zones:      nombre, tipo, ámbito de replicación, actualizaciones
 *                 dinámicas, servidores que la sirven y notas.
 *   - forwarders: servidor, IP, destino de reenvío y notas.
 */
import React from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

const ZONE_TYPES = [
  ['ad_integrated', 'Integrada en Active Directory'],
  ['primary', 'Primaria (archivo)'],
  ['secondary', 'Secundaria'],
  ['stub', 'Stub'],
  ['conditional_forwarder', 'Reenviador condicional'],
  ['reverse', 'Zona inversa (PTR)'],
];

const REPLICATIONS = [
  ['', '—'],
  ['domain', 'Todos los DC del dominio'],
  ['forest', 'Todos los DC del bosque'],
  ['legacy', 'Todos los DC del dominio (2000)'],
  ['custom', 'Personalizada'],
];

const DYNAMIC_UPDATES = [
  ['', '—'],
  ['secure', 'Sólo seguras'],
  ['nonsecure_secure', 'Seguras y no seguras'],
  ['none', 'Ninguna'],
];

function emptyZone() {
  return {
    name: '', zone_type: 'ad_integrated', replication: 'domain',
    dynamic_updates: 'secure', servers: '', notes: '',
  };
}

function emptyForwarder() {
  return { server: '', ip: '', target: '', notes: '' };
}

const EMPTY = { zones: [], forwarders: [], notes: '' };

export default function ADServiceDnsSection({ dns, onChange, editMode }) {
  const data = { ...EMPTY, ...(dns || {}) };
  const zones = Array.isArray(data.zones) ? data.zones : [];
  const forwarders = Array.isArray(data.forwarders) ? data.forwarders : [];

  const patch = (part) => onChange({ ...data, ...part });
  const updateZone = (idx, p) => {
    const next = zones.slice();
    next[idx] = { ...next[idx], ...p };
    patch({ zones: next });
  };
  const updateForwarder = (idx, p) => {
    const next = forwarders.slice();
    next[idx] = { ...next[idx], ...p };
    patch({ forwarders: next });
  };

  const shell = {
    background: 'rgba(56,189,248,.07)', border: '1px solid rgba(56,189,248,.35)',
    borderRadius: 8, padding: 12,
  };
  const th = {
    textAlign: 'left', padding: '4px 8px', color: 'var(--text-muted)',
    borderBottom: '1px solid var(--slate-300)', whiteSpace: 'nowrap',
  };
  const td = {
    padding: '4px 8px', borderBottom: '1px solid var(--slate-200)',
    color: 'var(--text-primary)',
  };

  const label = (list, value, fallback = '—') => {
    const hit = list.find(([v]) => v === value);
    return hit ? hit[1] : (value || fallback);
  };

  return (
    <div style={shell}>
      <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
        DNS del dominio
      </div>

      {/* ── Zonas ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <div style={{ flex: 1, fontSize: '0.74rem', fontWeight: 700, color: 'var(--text-primary)' }}>
          Zonas DNS
          <span style={{
            marginLeft: 6, fontSize: '0.7rem', background: 'var(--bg-input)',
            color: 'var(--text-muted)', borderRadius: 4, padding: '0 6px',
          }}>
            {zones.length}
          </span>
        </div>
        {editMode && (
          <button className="dv-btn" type="button" onClick={() => patch({ zones: [...zones, emptyZone()] })}>
            <PlusIcon style={{ width: 14, height: 14 }} /> Añadir zona
          </button>
        )}
      </div>

      {zones.length === 0 ? (
        <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
          Sin zonas documentadas (p. ej. corp.empresa.local integrada en AD, con
          replicación a todos los DC del bosque y actualizaciones dinámicas seguras).
        </div>
      ) : editMode ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
          {zones.map((z, idx) => (
            <div key={idx} style={{
              border: '1px solid var(--slate-200)', borderRadius: 6, padding: 8,
              background: 'var(--bg-card)',
            }}>
              <div className="row g-2">
                <div className="col-md-4">
                  <label className="form-label" style={{ fontSize: '0.7rem' }}>Zona</label>
                  <input
                    className="form-control form-control-sm"
                    placeholder="ej. corp.empresa.local"
                    value={z.name || ''}
                    onChange={e => updateZone(idx, { name: e.target.value })}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label" style={{ fontSize: '0.7rem' }}>Tipo</label>
                  <select
                    className="form-select form-select-sm"
                    value={z.zone_type || 'ad_integrated'}
                    onChange={e => updateZone(idx, { zone_type: e.target.value })}
                  >
                    {ZONE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label" style={{ fontSize: '0.7rem' }}>Ámbito de replicación</label>
                  <select
                    className="form-select form-select-sm"
                    value={z.replication || ''}
                    onChange={e => updateZone(idx, { replication: e.target.value })}
                  >
                    {REPLICATIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label" style={{ fontSize: '0.7rem' }}>Actualizaciones dinámicas</label>
                  <select
                    className="form-select form-select-sm"
                    value={z.dynamic_updates || ''}
                    onChange={e => updateZone(idx, { dynamic_updates: e.target.value })}
                  >
                    {DYNAMIC_UPDATES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label" style={{ fontSize: '0.7rem' }}>Servidores DNS</label>
                  <input
                    className="form-control form-control-sm"
                    placeholder="ej. DC01 (10.10.0.10), DC02"
                    value={z.servers || ''}
                    onChange={e => updateZone(idx, { servers: e.target.value })}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label" style={{ fontSize: '0.7rem' }}>Notas</label>
                  <input
                    className="form-control form-control-sm"
                    placeholder="ej. registros _msdcs, delegaciones…"
                    value={z.notes || ''}
                    onChange={e => updateZone(idx, { notes: e.target.value })}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                <button
                  className="dv-btn"
                  type="button"
                  onClick={() => patch({ zones: zones.filter((_, i) => i !== idx) })}
                  title="Eliminar zona"
                >
                  <TrashIcon style={{ width: 14, height: 14 }} /> Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ overflowX: 'auto', marginBottom: 14 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr>
                {['Zona', 'Tipo', 'Replicación', 'Act. dinámicas', 'Servidores', 'Notas'].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {zones.map((z, idx) => (
                <tr key={idx}>
                  <td style={{ ...td, fontWeight: 600 }}>{z.name}</td>
                  <td style={td}>{label(ZONE_TYPES, z.zone_type)}</td>
                  <td style={td}>{label(REPLICATIONS, z.replication)}</td>
                  <td style={td}>{label(DYNAMIC_UPDATES, z.dynamic_updates)}</td>
                  <td style={td}>{z.servers || '—'}</td>
                  <td style={{ ...td, color: 'var(--text-muted)' }}>{z.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Reenviadores ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <div style={{ flex: 1, fontSize: '0.74rem', fontWeight: 700, color: 'var(--text-primary)' }}>
          Reenviadores y resolutores
          <span style={{
            marginLeft: 6, fontSize: '0.7rem', background: 'var(--bg-input)',
            color: 'var(--text-muted)', borderRadius: 4, padding: '0 6px',
          }}>
            {forwarders.length}
          </span>
        </div>
        {editMode && (
          <button className="dv-btn" type="button" onClick={() => patch({ forwarders: [...forwarders, emptyForwarder()] })}>
            <PlusIcon style={{ width: 14, height: 14 }} /> Añadir
          </button>
        )}
      </div>

      {forwarders.length === 0 ? (
        <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
          Sin reenviadores documentados (p. ej. 8.8.8.8 como último recurso, o un
          reenviador condicional hacia el dominio del cliente).
        </div>
      ) : editMode ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {forwarders.map((f, idx) => (
            <div key={idx} className="row g-2" style={{ alignItems: 'flex-end' }}>
              <div className="col-md-3">
                <label className="form-label" style={{ fontSize: '0.7rem' }}>Servidor</label>
                <input
                  className="form-control form-control-sm"
                  placeholder="ej. DC01"
                  value={f.server || ''}
                  onChange={e => updateForwarder(idx, { server: e.target.value })}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label" style={{ fontSize: '0.7rem' }}>IP</label>
                <input
                  className="form-control form-control-sm"
                  placeholder="ej. 10.10.0.10"
                  value={f.ip || ''}
                  onChange={e => updateForwarder(idx, { ip: e.target.value })}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label" style={{ fontSize: '0.7rem' }}>Reenvía a</label>
                <input
                  className="form-control form-control-sm"
                  placeholder="ej. 8.8.8.8 / dominio cliente"
                  value={f.target || ''}
                  onChange={e => updateForwarder(idx, { target: e.target.value })}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label" style={{ fontSize: '0.7rem' }}>Notas</label>
                <input
                  className="form-control form-control-sm"
                  value={f.notes || ''}
                  onChange={e => updateForwarder(idx, { notes: e.target.value })}
                />
              </div>
              <div className="col-auto">
                <button
                  className="dv-btn"
                  type="button"
                  onClick={() => patch({ forwarders: forwarders.filter((_, i) => i !== idx) })}
                  title="Eliminar"
                >
                  <TrashIcon style={{ width: 14, height: 14 }} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr>{['Servidor', 'IP', 'Reenvía a', 'Notas'].map(h => <th key={h} style={th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {forwarders.map((f, idx) => (
                <tr key={idx}>
                  <td style={{ ...td, fontWeight: 600 }}>{f.server || '—'}</td>
                  <td style={{ ...td, fontFamily: 'monospace' }}>{f.ip || '—'}</td>
                  <td style={td}>{f.target || '—'}</td>
                  <td style={{ ...td, color: 'var(--text-muted)' }}>{f.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Notas DNS ── */}
      <div style={{ marginTop: 10 }}>
        <label className="form-label" style={{ fontSize: '0.7rem' }}>Notas de DNS</label>
        {editMode ? (
          <textarea
            className="form-control form-control-sm"
            rows={3}
            placeholder="ej. el DNS del dominio se sirve desde los propios DC; los clientes apuntan a los dos DC de su sede; el reenvío condicional al dominio del cliente se hace desde el DC de Madrid…"
            value={data.notes || ''}
            onChange={e => patch({ notes: e.target.value })}
          />
        ) : (
          data.notes ? (
            <pre style={{
              whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.8rem',
              color: 'var(--text-primary)', margin: 0,
            }}>
              {data.notes}
            </pre>
          ) : (
            <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
              Sin notas de DNS.
            </div>
          )
        )}
      </div>
    </div>
  );
}
